import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Catch-all React ErrorBoundary so an unhandled render-time exception
 * never leaves the user staring at a blank white screen. Logs the error
 * to the console and shows a friendly recovery screen with a "Try Again"
 * action that resets the boundary's local state. Reload reloads the page.
 */
export default class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      this.state.error?.message ||
      (typeof this.state.error === "string" ? this.state.error : "Something went wrong while rendering this page.");

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
      >
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">Something went wrong</h2>
          <p className="text-sm text-slate-600 break-words">{message}</p>
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={this.handleReset}
              data-testid="error-boundary-try-again"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Try Again
            </Button>
            <Button
              size="sm"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
            >
              Reload Page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
